import { Container } from 'pixi.js';
import { StudioSceneShape } from '../../../shape/shapes/general/root/studio-scene-shape';
import { saveShape } from '../studio.actions';
import { cloneDeep } from 'lodash';
import {
  StudioShapeBackground,
  StudioShapeDescriptor,
} from '../text/studio-text-shape';
import { StudioAnimation } from '..';
import gsap from 'gsap';
import { RectangleElement } from '../../../shape/shapes/primitive/rectangle-element';
import { RectangleController } from '../../../../elements/util/rectangle-controller/rectangle-controller';

export class StudioShape<
  T extends StudioShapeDescriptor = StudioShapeDescriptor,
> {
  container: Container;
  circleContainer: Container;
  loadPromise: Promise<void> = Promise.resolve();
  get x() {
    return 0;
  }
  get y() {
    return 0;
  }

  get cell() {
    return this.descriptor.cell;
  }
  get groupShapeParent() {
    return null;
  }

  get cs() {
    return this.shape.cs;
  }

  get service() {
    return this.shape.studioService.shapeService;
  }
  get studioService() {
    return this.shape.studioService;
  }

  get paddingValue() {
    return this.descriptor.cell.padding == undefined
      ? 0
      : (this.descriptor.cell.padding as number);
  }

  get absPadding() {
    return this.shape.sceneWidth * this.paddingValue;
  }

  get grossDims() {
    const { w, h } = this.descriptor.cell;
    return [this.cs.currentScreenX * w, this.cs.currentScreenY * h];
  }

  get grossCoords() {
    const { x, y } = this.descriptor.cell;
    const [screenX, screenY] = [this.cs.currentScreenX, this.cs.currentScreenY];
    const [xOffset, yOffset] = [0, 0];
    return [screenX * x + xOffset, screenY * y + yOffset];
  }

  get cellDims() {
    const [w, h] = this.grossDims;
    const [x, y] = this.grossCoords;
    return { x, y, w, h };
  }

  get padding(): [number, number] {
    if (!this.descriptor.cell.padding) {
      return [0, 0];
    }

    if (Array.isArray(this.descriptor.cell.padding)) {
      return this.descriptor.cell.padding as [number, number];
    }

    return [this.descriptor.cell.padding, this.descriptor.cell.padding];
  }

  get netDims() {
    const [w, h] = this.grossDims;
    const [px, py] = this.padding;
    return [
      w - 2 * px * this.cs.currentScreenX,
      h - 2 * py * this.cs.currentScreenY,
    ];
  }

  cellRc: RectangleController;
  constructor(
    protected readonly shape: StudioSceneShape,
    public id: string,

    public descriptor: T,
  ) {
    this.container = new Container();
    this.circleContainer = new Container();
    this.shape.container.addChild(this.container);
    this.shape.circleContainer.addChild(this.circleContainer);
    this.initBackgroundElement(this.descriptor.cell.background);
  }

  dragTookPlace = false;
  unmovedCoords: [number, number];

  pseudoAnimation(animation: StudioAnimation): gsap.core.Timeline {
    const { duration } = animation;

    const timeline = gsap.timeline();

    timeline.add(gsap.to({}, { duration: duration || 1 }));

    const { next, paralell } = animation;
    if (next) {
      const nextTimeline = this.pseudoAnimation(next);
      const nextOffset = next.overlap
        ? `-=${next.overlap}`
        : `+=${next.delay || 0}`;
      timeline.add(nextTimeline, nextOffset);
    }

    if (paralell) {
      const { overlap, delay } = paralell;
      const paralellTimeline = this.pseudoAnimation(paralell);
      const offset = overlap ? `-=${overlap}` : delay || 0;
      timeline.add(paralellTimeline, offset);
    }

    return timeline;
  }

  reset() {}

  update(descriptor: T) {
    this.descriptor = descriptor;
  }

  startBaseDrag() {
    this.dragTookPlace = false;
    // this.unmovedDims = [this.width, this.height]; //
    this.unmovedCoords = [this.container.x, this.container.y];
  }
  drag(dx: number, dy: number) {
    this.dragTookPlace = true;
    const [x, y] = this.unmovedCoords;
    this.setPosition(x + dx, y + dy);
  }

  endDrag() {
    if (!this.dragTookPlace) return;
    // Cell's x, y are relative to the shape's width and height
    const [w, h] = this.grossDims;
    const [x, y] = this.grossCoords;
    this.descriptor.cell.x = (this.container.x - x) / w;
    this.descriptor.cell.y = (this.container.y - y) / h;

    this.descriptor.cell.align = undefined;
    // -- // -- //
    this.save();
  }

  setPosition(x: number, y: number) {
    this.container.x = x;
    this.container.y = y;
    this.circleContainer.x = x;
    this.circleContainer.y = y;
  }

  save() {
    this.shape.scene.shapes[this.id] = this.descriptor;
    this.shape.store.dispatch(
      saveShape({
        shapeId: this.id,
        shape: cloneDeep(this.descriptor),
      }),
    );
  }

  backgroundElement: RectangleElement;

  initBackgroundElement(background: StudioShapeBackground): RectangleElement {
    this.backgroundElement?.remove();
    if (!background) return;
    const [width, height] = this.netDims;
    const { type, radius, color, opacity, gradient, stroke, padding } =
      background;

    if (type === 'gradient' && gradient) {
      const gradientStops = gradient.colors?.map(c => ({ color: c })) || [];
      this.backgroundElement = new RectangleElement(this, this.container, {
        width,
        height,
        r: radius || 0,
        gradient: true,
        gradientDirection: gradient.direction,
        gradientStops,
        stroke: stroke?.color,
        'stroke-width': stroke?.width,
      });
    } else {
      const { x, y, w, h } = this.cellDims;

      const paddingOffet = 0;

      // (padding || 0) *
      // (this.cs.isPortrait ? this.cs.currentScreenY : this.cs.currentScreenX);

      // -- // -- //
      this.backgroundElement = new RectangleElement(
        this,
        this.shape.container,
        {
          position: { x: x + paddingOffet, y: y + paddingOffet },
          width: w - 2 * paddingOffet,
          height: h - 2 * paddingOffet,
          r: radius || 0,
          fill: color || '#ffffff',
          'fill-opacity': opacity || 1,
          stroke: stroke?.color,
          'stroke-width': stroke?.width,
        },
      );
    }

    return this.backgroundElement;
  }

  removed = false;
  remove() {
    this.removed = true;
    this.container.destroy({ children: true });
    this.circleContainer.destroy({ children: true });
  }

  timeline: gsap.core.Timeline;
  gsapAnimateStep(animation: StudioAnimation, timeline?: gsap.core.Timeline) {
    timeline ||= gsap.timeline();
    const { next, paralell, sceneTransition, type, duration } = animation;

    if (next) {
      const nextTarget = this.shape.getTargetIRI(next.target);
      const nextShape = this.shape.shapes[nextTarget];
      if (nextShape) {
        const nextTimeline = nextShape.gsapAnimateStep(next);
        const { overlap, delay } = next;
        const offset = overlap ? `-=${overlap}` : `+=${delay || 0}`;
        timeline.add(nextTimeline, offset);
      } else {
        console.warn('Next shape not found', next.target);
      }
    }

    if (paralell) {
      const paralellTarget = this.shape.getTargetIRI(paralell.target);
      const paralellShape = this.shape.shapes[paralellTarget];
      if (paralellShape) {
        const paralellTimeline = paralellShape.gsapAnimateStep(paralell);
        timeline.add(paralellTimeline, paralell.delay || 0);
      } else {
        // console.warn('Paralell shape not found', paralell.target);
      }
    }

    // -- //

    return timeline;
  }
}
